import { useQueryClient } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import type { Address } from "viem"

import type { KarmaConfig, KarmaStats } from "@/governance/helpers/karma"
import { fetchKarmaScores } from "@/governance/helpers/karma"
import type { Delegate } from "@/query/graphql"

type useKarmaScoresProps = {
  karmaName: string | undefined
  delegates?: Delegate[]
  addresses?: string[] | Address[]
  config?: KarmaConfig
}

const useKarmaScores = ({
  karmaName,
  delegates,
  addresses,
  config,
}: useKarmaScoresProps) => {
  const [karmaScores, setKarmaScores] = useState<{
    [address: string]: KarmaStats
  }>({})
  const [karmaScoresFetched, setKarmaScoresFetched] = useState<{
    [address: string]: boolean
  }>({})
  const [isLoadingKarmaScores, setIsLoadingKarmaScores] = useState(false)
  const queryClient = useQueryClient()

  // biome-ignore lint: biome-migration
  useEffect(() => {
    const fetchKarmaData = async () => {
      if (!karmaName) {
        return
      }

      const _addressList =
        delegates?.map((delegate) => delegate.account.address) ||
        addresses ||
        []

      const _addresses = _addressList
        .filter((address) => Boolean(address))
        .filter((address) => !karmaScores[address.toLowerCase()])

      if (_addresses.length === 0) {
        return
      }

      setIsLoadingKarmaScores(true)

      try {
        const newKarmaScores = await fetchKarmaScores(
          karmaName,
          _addresses,
          config,
        )

        Object.entries(newKarmaScores ?? {}).forEach(
          ([address, karmaScore]) => {
            queryClient.setQueryData(
              ["karma-score", karmaName, address.toLowerCase()],
              karmaScore,
            )
          },
        )

        if (newKarmaScores) {
          setKarmaScores((prevScores) => ({ ...prevScores, ...newKarmaScores }))

          setKarmaScoresFetched((prevFetched) => {
            const updatedFetched = { ...prevFetched }

            _addresses.forEach((address) => {
              updatedFetched[address.toLowerCase()] = true
            })

            return updatedFetched
          })
        }
      } catch (error) {
        console.error("Failed to fetch karma scores", error)
      } finally {
        setIsLoadingKarmaScores(false)
      }
    }

    fetchKarmaData()
  }, [delegates, karmaName, addresses, config])

  return { karmaScores, karmaScoresFetched, isLoadingKarmaScores }
}

export default useKarmaScores
